import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RichTextEditorComponent),
      multi: true,
    },
  ],
})
export class RichTextEditorComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() minHeight = '300px';

  @ViewChild('editor', { static: true }) editorRef!: ElementRef<HTMLDivElement>;

  private savedRange: Range | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  disabled = false;

  writeValue(value: string | null): void {
    const html = value ?? '';
    if (this.editorRef && this.editorRef.nativeElement.innerHTML !== html) {
      this.editorRef.nativeElement.innerHTML = html;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(): void {
    this.onChange(this.editorRef.nativeElement.innerHTML);
  }

  onBlur(): void {
    this.saveSelection();
    this.onTouched();
  }

  exec(command: string, value?: string): void {
    this.editorRef.nativeElement.focus();
    this.restoreSelection();
    document.execCommand(command, false, value);
    this.onChange(this.editorRef.nativeElement.innerHTML);
    this.saveSelection();
  }

  insertLink(): void {
    const url = window.prompt('Enter the URL:');
    if (url) {
      this.exec('createLink', url);
    }
  }

  insertHtml(html: string): void {
    this.editorRef.nativeElement.focus();
    this.restoreSelection();
    document.execCommand('insertHTML', false, html);
    this.onChange(this.editorRef.nativeElement.innerHTML);
    this.saveSelection();
  }

  insertText(text: string): void {
    this.insertHtml(this.escapeHtml(text));
  }

  private saveSelection(): void {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (this.editorRef.nativeElement.contains(range.commonAncestorContainer)) {
      this.savedRange = range.cloneRange();
    }
  }

  private restoreSelection(): void {
    if (!this.savedRange) {
      const range = document.createRange();
      range.selectNodeContents(this.editorRef.nativeElement);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      return;
    }
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(this.savedRange);
  }

  @HostListener('document:selectionchange')
  onSelectionChange(): void {
    this.saveSelection();
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
